/**
 * ConnectionToast
 *
 * A small, bottom-right toast notification for transient WebSocket events:
 *
 *  • "Reconnecting… attempt 3 of 15, retrying in 4s"
 *  • "Connection restored"
 *  • "Unable to connect" + Retry button
 *  • "You are offline"
 *
 * Auto-dismisses after recovery. Zero layout shift — uses position: fixed
 * in the bottom-right corner, well away from the header.
 */

import React, { useEffect, useState } from "react";
import { useWebSocket } from "../../contexts/WebSocketProvider";
import type { ConnectionState } from "../../services/socketService";
import "./ConnectionToast.css";

/** How long to show the "reconnected" success toast (ms) */
const RECONNECTED_DISPLAY_MS = 3_000;

/* ── Pure helper (exported for tests) ──────────────────────────────────── */

export interface ToastConfig {
  message: string;
  detail?: string;
  variant: "warning" | "error" | "info" | "success";
  showRetry?: boolean;
}

export function getToastConfig(
  state: ConnectionState,
  reconnectAttempt: number,
  maxAttempts: number,
  reconnectInSeconds: number | null,
  isNetworkOnline: boolean,
): ToastConfig | null {
  if (!isNetworkOnline) {
    return {
      message: "You are offline",
      detail: "We'll reconnect automatically when you're back online.",
      variant: "error",
    };
  }

  switch (state) {
    case "reconnecting":
      return {
        message: "Reconnecting…",
        detail:
          reconnectInSeconds != null
            ? `Attempt ${reconnectAttempt} of ${maxAttempts} · retrying in ${reconnectInSeconds}s`
            : `Attempt ${reconnectAttempt} of ${maxAttempts}`,
        variant: "warning",
      };

    case "failed":
      return {
        message: "Unable to connect",
        detail: "All reconnection attempts failed.",
        variant: "error",
        showRetry: true,
      };

    // Don't toast for connecting/authenticating — the header dot handles it
    default:
      return null;
  }
}

/* ── Component ─────────────────────────────────────────────────────────── */

export const ConnectionToast: React.FC = () => {
  const { health, reconnect } = useWebSocket();
  const [showReconnected, setShowReconnected] = useState(false);
  const [prevState, setPrevState] = useState<ConnectionState>(health.state);

  // Detect recovery: was reconnecting/failed → now authenticated
  useEffect(() => {
    const wasDown = prevState === "reconnecting" || prevState === "failed";
    const isUp =
      health.state === "authenticated" || health.state === "connected";

    if (wasDown && isUp) {
      setShowReconnected(true);
      const timer = setTimeout(
        () => setShowReconnected(false),
        RECONNECTED_DISPLAY_MS,
      );
      return () => clearTimeout(timer);
    }

    setPrevState(health.state);
  }, [health.state, prevState]);

  // "Connection restored" toast
  if (showReconnected) {
    return (
      <div
        className="connection-toast connection-toast--success"
        role="status"
        data-testid="connection-toast"
      >
        <span className="connection-toast__icon">✓</span>
        <span className="connection-toast__message">Connection restored</span>
      </div>
    );
  }

  const config = getToastConfig(
    health.state,
    health.reconnectAttempt,
    health.maxReconnectAttempts,
    health.reconnectInSeconds,
    health.isNetworkOnline,
  );

  if (!config) return null;

  return (
    <div
      className={`connection-toast connection-toast--${config.variant}`}
      role="alert"
      aria-live="polite"
      data-testid="connection-toast"
    >
      <div className="connection-toast__body">
        <span className="connection-toast__message">{config.message}</span>
        {config.detail && (
          <span className="connection-toast__detail">{config.detail}</span>
        )}
      </div>
      {config.showRetry && (
        <button
          className="connection-toast__retry"
          onClick={reconnect}
          type="button"
        >
          Retry
        </button>
      )}
    </div>
  );
};

export default ConnectionToast;
